import { BetaFeatureDTO, DefaultVersionEnum, FeaturesEnum } from '@api/ringil-api.v2';

/**
 * FE-owned catalog of Beta-feature surfaces.
 */
export const BetaSurface = {
  DASHBOARD: 'DASHBOARD',
  COMPANY_LOCATION_DETAIL: 'COMPANY_LOCATION_DETAIL',
  DATA_TABLE_FILTERS_V2: 'DATA_TABLE_FILTERS_V2',
  EXPEDITION_EXTRA_COSTS: 'EXPEDITION_EXTRA_COSTS',
  EXPEDITION_TENDER_HISTORY: 'EXPEDITION_TENDER_HISTORY',
  EXPEDITION_AUDIT_LOG: 'EXPEDITION_AUDIT_LOG',
  SUPPLIER_NOTIFICATION_AUDIT_LOG: 'SN_AUDIT_LOG',
  RESERVATION_AUDIT_LOG: 'RESERVATION_AUDIT_LOG',
  NOTIFICATION_CENTER: 'NOTIFICATION_CENTER',
  TIMESLOT_CALENDAR_CARD: 'TIMESLOT_CALENDAR_CARD',
  EXPEDITION_MULTI_DUPLICATION: 'EXPEDITION_MULTI_DUPLICATION',
  EXPORT_JOB_CENTER: 'EXPORT_JOB_CENTER',
  CREATE_EXPEDITION_ERROR_SUMMARY: 'CREATE_EXPEDITION_ERROR_SUMMARY',
  RESERVATION_MULTI_DUPLICATION: 'RESERVATION_MULTI_DUPLICATION',
  // One switch for every list on the new table (expeditions, supplier notifications, later reservations).
  // The stored key keeps its first name, so companies that turned it on keep it on.
  SPLIT_VIEW: 'EXPEDITION_SPLIT_VIEW',
} as const;

export type BetaSurfaceKey = (typeof BetaSurface)[keyof typeof BetaSurface];

/** The opaque per-company store: surface key → its config. Codegen types this as `any`. */
export type BetaFeaturesMap = Record<string, BetaFeatureDTO>;

export interface BetaSurfaceDef {
  key: BetaSurfaceKey;
  /** i18n key BetaFeatures.surface.<KEY>; fallback is the English default. */
  labelFallback: string;
  /** Version rendered when the company hasn't configured this surface. */
  defaultVersion: DefaultVersionEnum;
  /** Whether the user-facing switch shows when the company hasn't configured this surface. */
  switchVisible: boolean;
  /** False for surfaces with no end-user switch (NEW = collapsed, ORIGINAL = expanded, admin-only).
   * The admin hides the Show-switch toggle and the resolver ignores any per-user override. Defaults true. */
  userSwitchable?: boolean;
  /** Company feature acting as kill switch for this surface: without it the per-surface config is ignored
   * and the surface resolves to ORIGINAL. */
  requiredFeature?: FeaturesEnum;
}

export const BETA_SURFACES: BetaSurfaceDef[] = [
  { key: BetaSurface.DASHBOARD, labelFallback: 'Dashboard', defaultVersion: DefaultVersionEnum.ORIGINAL, switchVisible: true },
  { key: BetaSurface.COMPANY_LOCATION_DETAIL, labelFallback: 'Company location detail', defaultVersion: DefaultVersionEnum.ORIGINAL, switchVisible: true },
  { key: BetaSurface.DATA_TABLE_FILTERS_V2, labelFallback: 'Data table filters', defaultVersion: DefaultVersionEnum.NEW, switchVisible: true },
  // detail sections: NEW = collapsed, ORIGINAL = always expanded. No end-user switch.
  {
    key: BetaSurface.EXPEDITION_EXTRA_COSTS,
    labelFallback: 'Expedition – total costs',
    defaultVersion: DefaultVersionEnum.ORIGINAL,
    switchVisible: false,
    userSwitchable: false,
    requiredFeature: FeaturesEnum.LAZY_LOAD_DETAIL_SECTIONS,
  },
  {
    key: BetaSurface.EXPEDITION_TENDER_HISTORY,
    labelFallback: 'Expedition – selection procedure history',
    defaultVersion: DefaultVersionEnum.ORIGINAL,
    switchVisible: false,
    userSwitchable: false,
    requiredFeature: FeaturesEnum.LAZY_LOAD_DETAIL_SECTIONS,
  },
  {
    key: BetaSurface.EXPEDITION_AUDIT_LOG,
    labelFallback: 'Expedition – audit log',
    defaultVersion: DefaultVersionEnum.ORIGINAL,
    switchVisible: false,
    userSwitchable: false,
    requiredFeature: FeaturesEnum.LAZY_LOAD_DETAIL_SECTIONS,
  },
  {
    key: BetaSurface.SUPPLIER_NOTIFICATION_AUDIT_LOG,
    labelFallback: 'Supplier notification – audit log',
    defaultVersion: DefaultVersionEnum.ORIGINAL,
    switchVisible: false,
    userSwitchable: false,
    requiredFeature: FeaturesEnum.LAZY_LOAD_DETAIL_SECTIONS,
  },
  {
    key: BetaSurface.RESERVATION_AUDIT_LOG,
    labelFallback: 'Reservation – audit log',
    defaultVersion: DefaultVersionEnum.ORIGINAL,
    switchVisible: false,
    userSwitchable: false,
    requiredFeature: FeaturesEnum.LAZY_LOAD_DETAIL_SECTIONS,
  },
  { key: BetaSurface.NOTIFICATION_CENTER, labelFallback: 'Notification center', defaultVersion: DefaultVersionEnum.NEW, switchVisible: false },
  // NEW = the event card and its popover are laid out by the configured layout model; ORIGINAL = the
  // fixed layout the calendar has always drawn. Switched per company by an admin, not by the user:
  // which card a calendar draws is a company-wide decision, and a per-user override would make
  // "what does my calendar look like" unanswerable in support.
  {
    key: BetaSurface.TIMESLOT_CALENDAR_CARD,
    labelFallback: 'Timeslot calendar – event card',
    defaultVersion: DefaultVersionEnum.ORIGINAL,
    switchVisible: false,
    userSwitchable: false,
  },
  // NEW = duplication runs as an async job with a job detail page; ORIGINAL = synchronous save in the sidebar.
  { key: BetaSurface.EXPEDITION_MULTI_DUPLICATION, labelFallback: 'Multi-duplication (async job)', defaultVersion: DefaultVersionEnum.ORIGINAL, switchVisible: true },
  // NEW = list exports run as async jobs in the jobs center (live progress, download there); ORIGINAL = legacy export banner.
  { key: BetaSurface.EXPORT_JOB_CENTER, labelFallback: 'Export via jobs center', defaultVersion: DefaultVersionEnum.ORIGINAL, switchVisible: true },
  // NEW = failed submits also list every error at the top of the form; ORIGINAL = the toast and the red
  // fields on their own. No end-user switch: which of the two a company sees is not a per-person choice,
  // and the toast stays either way, so there is nothing for a user to miss by not having the toggle.
  {
    key: BetaSurface.CREATE_EXPEDITION_ERROR_SUMMARY,
    labelFallback: 'Create shipment – validation error summary',
    defaultVersion: DefaultVersionEnum.ORIGINAL,
    switchVisible: false,
    userSwitchable: false,
  },
  // NEW = "Repeating reservations" creates an async job (one reservation per slot, live job detail page);
  // ORIGINAL = the dialog creates the reservations itself, one request per slot.
  { key: BetaSurface.RESERVATION_MULTI_DUPLICATION, labelFallback: 'Repeating reservations (async job)', defaultVersion: DefaultVersionEnum.ORIGINAL, switchVisible: true },
  // NEW = the lists render on RingilDataTable v3 (tabs, column config, the split layouts) — the customer
  // expedition list and the supplier notification list; ORIGINAL = RingilDataTable v2.
  { key: BetaSurface.SPLIT_VIEW, labelFallback: 'Lists – new table and split view', defaultVersion: DefaultVersionEnum.ORIGINAL, switchVisible: true },
];

export const BETA_SURFACE_BY_KEY: Record<BetaSurfaceKey, BetaSurfaceDef> = BETA_SURFACES.reduce(
  (acc, s) => ({ ...acc, [s.key]: s }),
  {} as Record<BetaSurfaceKey, BetaSurfaceDef>,
);
